import { useState } from 'react';
import { BRAND, type NodeDto } from '@br/protocol';
import { api, errorMessage } from '../api/client';
import { useMutation } from '@tanstack/react-query';
import { useDeleteNode, useNodes, useRenameNode, useRotateNodeToken } from '../api/queries';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  Dot,
  EmptyState,
  Field,
  FullPageSpinner,
  Input,
  Modal,
  PageHeader,
} from '../components/ui';
import { formatRelative } from '../lib/format';
import { apiBase, toWsOrigin } from '../lib/config';
import { usePageTitle } from '../lib/title';

export function NodesPage() {
  usePageTitle('노드');
  const { isAdmin } = useAuth();
  const toast = useToast();
  const nodes = useNodes();
  const rename = useRenameNode();
  const remove = useDeleteNode();
  const rotate = useRotateNodeToken();
  const [forceNode, setForceNode] = useState<NodeDto | null>(null);
  const update = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      api('POST', `/api/nodes/${id}/update`, { force }),
    onSuccess: () => {
      setForceNode(null);
      toast.success('업데이트 요청을 전달했습니다');
      void nodes.refetch();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const [renaming, setRenaming] = useState<NodeDto | null>(null);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<NodeDto | null>(null);
  const [rotating, setRotating] = useState<NodeDto | null>(null);
  const [token, setToken] = useState<{ node: NodeDto; token: string } | null>(null);
  const apiOrigin = apiBase() || window.location.origin;
  const fail = (err: unknown) => toast.error(errorMessage(err));

  return (
    <div className="space-y-5">
      <PageHeader
        title="노드"
        description={`${BRAND.nodeName}가 설치된 운영 PC입니다. 각 브라우저 프로필은 지정된 노드에서 Chrome을 실행합니다.`}
      />
      {nodes.isPending && <FullPageSpinner />}
      {nodes.isError && <Alert level="error">{errorMessage(nodes.error)}</Alert>}
      {nodes.data && (
        <Card padded={false}>
          {nodes.data.length === 0 ? (
            <EmptyState
              title="등록된 노드가 없습니다"
              hint={`아래 안내에 따라 ${BRAND.nodeName}를 설치하세요`}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas text-left text-xs text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">노드</th>
                    <th className="px-4 py-2 font-medium">Node 버전</th>
                    <th className="px-4 py-2 font-medium">PC</th>
                    <th className="px-4 py-2 font-medium">Chrome</th>
                    <th className="px-4 py-2 font-medium">headless</th>
                    <th className="px-4 py-2 font-medium">열린 프로필</th>
                    <th className="px-4 py-2 font-medium">마지막 접속</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {nodes.data.map((n) => (
                    <tr key={n.id} className="border-t border-line">
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-2 font-medium text-ink">
                          <Dot online={n.online} />
                          {n.name}
                        </span>
                        {!n.online && <span className="ml-2 text-xs text-ink-muted">오프라인</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <span className="font-mono">{n.nodeVersion ?? '-'}</span>
                        <p>
                          {n.update
                            ? {
                                idle: '업데이트 대기',
                                checking: '확인 중',
                                available: '새 버전 있음',
                                downloading: `다운로드 ${n.update.percent ?? 0}%`,
                                downloaded: '설치 준비됨',
                                pending_close: '프로필 종료 대기',
                                installing: '설치 중',
                                error: '업데이트 실패',
                                disabled: '업데이트 사용 불가',
                              }[n.update.status]
                            : ''}
                        </p>
                        {n.update?.error && (
                          <p className="max-w-64 text-danger">{n.update.error}</p>
                        )}
                        {n.online && !n.remoteUpdate && <p>노드 앱에서 먼저 업데이트해주세요</p>}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <div>{n.hostname ?? '-'}</div>
                        <div className="text-ink-muted">{n.os ?? ''}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        {n.chromeAvailable === null ? (
                          <span className="text-ink-muted">?</span>
                        ) : n.chromeAvailable ? (
                          <Badge tone="success">있음</Badge>
                        ) : (
                          <Badge tone="danger">없음</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {n.headless === null ? (
                          <span className="text-ink-muted">?</span>
                        ) : n.headless ? (
                          <Badge tone="warning">켬</Badge>
                        ) : (
                          <Badge tone="neutral">끔</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{n.openProfileCount}</td>
                      <td className="px-4 py-2.5 text-ink-muted" title={n.lastSeenAt ?? ''}>
                        {n.online ? '지금' : formatRelative(n.lastSeenAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1">
                          {isAdmin && (
                            <>
                              <Button
                                size="sm"
                                disabled={!n.online || !n.remoteUpdate || update.isPending}
                                onClick={() => update.mutate({ id: n.id, force: false })}
                              >
                                업데이트 요청
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                disabled={!n.online || !n.remoteUpdate || update.isPending}
                                onClick={() => setForceNode(n)}
                              >
                                강제 업데이트
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            onClick={() => {
                              setRenaming(n);
                              setName(n.name);
                            }}
                          >
                            이름 변경
                          </Button>
                          {isAdmin && (
                            <Button size="sm" onClick={() => setRotating(n)}>
                              토큰 재발급
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="danger" onClick={() => setDeleting(n)}>
                              삭제
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card title="노드 추가">
        <div className="space-y-4 text-sm text-ink">
          <div>
            <p className="font-medium">운영 PC (Windows)</p>
            <p className="mt-1 text-ink-muted">
              vos-tools 런처에서 <code className="rounded bg-canvas px-1">{BRAND.slug}</code> 앱(
              {BRAND.nodeName})을 설치하면 PC의{' '}
              <code className="rounded bg-canvas px-1">license.jwt</code>로 이 서버에 자동
              등록됩니다. 별도 로그인이나 토큰 입력이 필요 없습니다. 등록되면 이 목록에 PC 이름으로
              나타납니다.
            </p>
          </div>
          <div>
            <p className="font-medium">개발·테스트용 (토큰 방식)</p>
            <p className="mt-1 text-ink-muted">
              운영자 계정으로 노드 토큰을 발급받아 Node CLI에 넘깁니다. 토큰은 발급 시 한 번만
              표시됩니다.
            </p>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-screen p-3 font-mono text-xs text-white">
              {`curl -X POST ${apiOrigin}/api/nodes/register \\
  -H 'content-type: application/json' \\
  -d '{"email":"admin@example.com","password":"...","nodeName":"내 PC"}'

BR_SERVER_URL=${toWsOrigin(apiOrigin)} BR_NODE_TOKEN=<token> pnpm --filter @br/node-core start`}
            </pre>
          </div>
        </div>
      </Card>

      <Modal
        open={renaming !== null}
        title="노드 이름 변경"
        onClose={() => setRenaming(null)}
        size="sm"
        footer={
          <>
            <Button onClick={() => setRenaming(null)}>취소</Button>
            <Button
              variant="primary"
              loading={rename.isPending}
              onClick={() =>
                renaming &&
                rename.mutate(
                  { id: renaming.id, name: name.trim() },
                  { onSuccess: () => setRenaming(null), onError: fail },
                )
              }
            >
              저장
            </Button>
          </>
        }
      >
        <Field label="이름" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={64} autoFocus />
        </Field>
      </Modal>

      <ConfirmDialog
        open={rotating !== null}
        title="노드 토큰을 재발급할까요?"
        message={`기존 토큰은 즉시 무효화되고 ${BRAND.nodeName} 연결이 끊어집니다. 새 토큰을 Node에 다시 설정해야 합니다.`}
        confirmLabel="재발급"
        danger
        loading={rotate.isPending}
        onCancel={() => setRotating(null)}
        onConfirm={() =>
          rotating &&
          rotate.mutate(rotating.id, {
            onSuccess: (t) => {
              setToken({ node: rotating, token: t });
              setRotating(null);
            },
            onError: fail,
          })
        }
      />

      <ConfirmDialog
        open={forceNode !== null}
        title="열린 프로필을 종료하고 업데이트할까요?"
        message={`${forceNode?.name ?? ''}: 새 버전 다운로드가 끝나면 열린 프로필 ${forceNode?.openProfileCount ?? 0}개를 종료하고 노드를 다시 시작합니다. 쿠키와 프로필 데이터는 유지되며 입력 중인 내용은 유실될 수 있습니다. 최신 버전이면 종료하지 않습니다.`}
        confirmLabel="강제 업데이트"
        danger
        loading={update.isPending}
        onCancel={() => setForceNode(null)}
        onConfirm={() => forceNode && update.mutate({ id: forceNode.id, force: true })}
      />

      <Modal
        open={token !== null}
        title="새 노드 토큰"
        onClose={() => setToken(null)}
        size="sm"
        footer={<Button onClick={() => setToken(null)}>닫기</Button>}
      >
        {token && (
          <div className="space-y-3 text-sm">
            <Alert level="warning">
              이 토큰은 지금 한 번만 표시됩니다. 안전한 곳에 복사해두세요.
            </Alert>
            <p className="text-ink-muted">노드: {token.node.name}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded bg-canvas p-2 font-mono text-xs">
                {token.token}
              </code>
              <CopyButton text={token.token} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="노드를 삭제할까요?"
        message={`${deleting?.name ?? ''} 노드를 삭제합니다. 연결된 ${BRAND.nodeName}는 즉시 끊기며, 이 노드의 브라우저 프로필은 더 이상 열 수 없습니다.`}
        confirmLabel="삭제"
        danger
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null), onError: fail })
        }
      />
    </div>
  );
}
